import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  styled,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from "@mui/material";

import { Delete, Edit } from "@mui/icons-material";
import { InternalProjectDto } from "../../types/traineesTypes";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import InfoSection, { InformationField } from "../../components/general/InfoSection";
import LoadingCircle from "../../components/general/LoadingCircle";
import EditInternalProjectDialog from "../../components/members/trainees/EditInternalProjectDialog";
import { AxiosResponse } from "axios";
import useTrainees from "../../hooks/trainees/useTrainees";
import useInternalProjectDetails from "../../hooks/trainees/useInternalProjectDetails";
import useMembers from "../../hooks/members/useMembers";
import { MembersFieldDto } from "../../types/membersTypes";
import { formatDate } from "../../utils/dateUtils";

// Styling for the paper element
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  maxWidth: "600px",
}));

/**
 * Function that allows displays internal project details
 * @returns returns the interface for the user
 */
const InternalProject: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { auth } = useContext(AuthContext);
  const hasInternalProjectPermission = doesPermissionsHaveSomeOf(auth.permissions, [15]);

  const [internalProjectInfoDialogOpen, setInternalProjectInfoDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectableTrainees, setSelectableTrainees] = useState<MembersFieldDto[]>([]);
  const [selectableQMs, setSelectableQMs] = useState<MembersFieldDto[]>([]);

  const { trainees } = useTrainees();
  const { members } = useMembers();
  const {
    internalProjectDetails,
    isInternalProjectDetailsFetched,
    updateInternalProjectDetails,
    deleteInternalProject,
  } = useInternalProjectDetails(Number(id));

  const navigate = useNavigate();

  useEffect(() => {
    setSelectableQMs(
      members
        // filter all members that have the status "Ausgetretene" or "Trainee"
        .filter((member) => member.memberStatus?.name !== "Trainee" && member.memberStatus?.name !== "Ausgetretene")
        // map the members to the MembersFieldDto
        .map((member) => {
          return {
            memberId: member.memberId,
            firstname: member.firstname,
            lastname: member.lastname,
            memberStatus: member.memberStatus,
          };
        })
    );
  }, [members]);

  useEffect(() => {
    setSelectableTrainees(
      trainees
        // filter out all trainees that have the status "Ausgetretene" or are already part of the internal project
        .filter((trainee) => trainee.memberStatus?.name !== "Ausgetretene")
        // filter out all trainees that are already part of the internal project
        .filter(
          (trainee) =>
            trainee.memberId !==
            internalProjectDetails?.members?.map((member) => member.memberId).find((id) => id === trainee.memberId)
        )
    );
  }, [trainees]);

  /**
   * Handles the click on the edit button of the internal project information section
   * @param event MouseEvent
   */
  const handleInternalProjectInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setInternalProjectInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the edit dialog
   */
  const handleInternalProjectInfoDialogClose = () => {
    setInternalProjectInfoDialogOpen(false);
  };

  /**
   * Handles the opening of the delete dialog
   */
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  /**
   * Handles the closing of the delete dialog
   */
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleUpdateInternalProjectDetails = (ipDetails: InternalProjectDto) => {
    updateInternalProjectDetails(ipDetails).then((response: AxiosResponse) => {
      response.status === 204 ? handleInternalProjectInfoDialogClose() : null;
    });
  };

  /**
   * Handles the deletion of the internal project
   * @returns void
   */
  const handleDeleteInternalProject = () => {
    deleteInternalProject(Number(id)).then(() => {
      handleDeleteDialogClose();
      handleInternalProjectInfoDialogClose();
      navigate("/traineebereich");
    });
  };

  // Fields for the internal project details
  const internalProjectDetailsFields: Array<InformationField> = [
    {
      label: "Internesprojekt",
      value: internalProjectDetails?.projectName,
      type: "text",
    },
    {
      label: "Kürzel",
      value: internalProjectDetails?.abbreviation,
      type: "text",
    },
    {
      label: "Traineegeneration",
      value: internalProjectDetails?.generationName,
      type: "text",
    },
    {
      label: "Kickoff",
      value: formatDate(internalProjectDetails?.kickoff),
      type: "text",
    },
    {
      label: "Angebot abgegeben",
      value: internalProjectDetails?.offerAtEv,
      type: "checkbox",
    },
    {
      label: "ZP abgegeben",
      value: internalProjectDetails?.zpAtEv,
      type: "checkbox",
    },
    {
      label: "ZP Datum",
      value: formatDate(internalProjectDetails?.zpHeld),
      type: "text",
    },
    {
      label: "AP abgegeben",
      value: internalProjectDetails?.apAtEv,
      type: "checkbox",
    },
    {
      label: "AP Datum",
      value: formatDate(internalProjectDetails?.apHeld),
      type: "text",
    },
    {
      label: "DL abgegeben",
      value: internalProjectDetails?.dlAtEv,
      type: "checkbox",
    },
    {
      label: "Projektmitglieder",
      value: internalProjectDetails?.members || [],
      type: "memberList",
    },
    {
      label: "Qualitätsmanager",
      value: internalProjectDetails?.qualityManagers || [],
      type: "memberList",
    },
  ];

  /*
   * Returns the internal project details if the retrieval of the internal project was successful (internalProjectDetails is not null or undefined)
   */
  return isInternalProjectDetailsFetched ? (
    <Container maxWidth="lg">
      {internalProjectDetails ? (
        <EditInternalProjectDialog
          internalProjectDetails={internalProjectDetails}
          open={internalProjectInfoDialogOpen}
          closeDialog={handleInternalProjectInfoDialogClose}
          updateInternalProjectDetails={handleUpdateInternalProjectDetails}
          selectableQMs={selectableQMs}
          selectableTrainees={selectableTrainees}
        />
      ) : null}

      <StyledPaper>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 2,
          }}
        >
          <Typography variant="h6" fontWeight={"bold"}>
            Informationen zum internen Projekt
          </Typography>
          {hasInternalProjectPermission ? (
            <Box>
              <IconButton
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                  handleInternalProjectInfoDialogOpen(event)
                }
              >
                <Edit fontSize="inherit" />
              </IconButton>
              {internalProjectDetails?.apHeld ? null : (
                <IconButton onClick={handleDeleteDialogOpen}>
                  <Delete fontSize="inherit" />
                </IconButton>
              )}
              <Dialog open={deleteDialogOpen}>
                <DialogTitle>Internes Projekt löschen</DialogTitle>
                <DialogContent>
                  <Typography>
                    Bist Du sicher, dass Du das interne Projekt <strong>"{internalProjectDetails?.projectName}"</strong>{" "}
                    löschen möchtest?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button variant="contained" onClick={handleDeleteInternalProject} color="error">
                    Löschen
                  </Button>
                  <Button onClick={handleDeleteDialogClose} variant="contained" color="secondary">
                    Abbrechen
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          ) : null}
        </Box>
        <Box>
          <InfoSection fields={internalProjectDetailsFields} />
        </Box>
      </StyledPaper>
    </Container>
  ) : (
    <LoadingCircle />
  );
};

export default InternalProject;
